/**
 * pdp-spec.js — PDP spec card, accordion, and spec table data functions.
 *
 * Included by both product-details.liquid and specification-dimensions.liquid,
 * so the browser may execute this script twice. The __pdpSpecRegistered guard
 * prevents duplicate declarations while assigning to window.* ensures Alpine
 * can resolve the factory functions via x-data="fnName()".
 */

if (!window.__pdpSpecRegistered) {
  window.__pdpSpecRegistered = true;

  // ─── Shared spec-rows factory ───────────────────────────────────────────────
  // pdSpecCard and specTable expose identical reactive behaviour; the only
  // difference is which JSON blob they read from.
  //
  // @param {string} blobId — id of the <script type="application/json"> element
  window.makeSpecRows = (blobId) => ({
    rows: [],
    activeData: {},

    get visibleRows() {
      return this.rows.filter((row) => this.getValue(row) !== '');
    },

    getValue(row) {
      if (row.static !== null) return row.static;
      return this.activeData[row.key] ?? '';
    },

    init() {
      const blob = document.getElementById(blobId);
      if (!blob) return;

      let data;
      try {
        data = JSON.parse(blob.textContent);
      } catch (e) {
        console.error(`[pdp-spec] ${blobId} parse error:`, e);
        return;
      }

      this.rows = data.rows ?? [];

      // Prefer the exact default variant; fall back to the first entry in the
      // variants map (handles selected variant !== grouped first-available).
      this.activeData = data.variants?.[data.default] ?? Object.values(data.variants ?? {})[0] ?? {};

      document.addEventListener('grouped-product:changed', (e) => {
        this.activeData = data.variants?.[e.detail.variantId] ?? this.activeData;
      });
    },
  });

  // ─── pdSpecCard — at-a-glance spec card ────────────────────────────────────
  window.pdSpecCard = () => window.makeSpecRows('product-details-json');

  // ─── specTable — full specification & dimensions table ──────────────────────
  window.specTable = () => window.makeSpecRows('spec-table-json');

  // ─── pdDetails — accordion column ──────────────────────────────────────────
  // Distinct from the spec-rows pattern: manages open/close state and renders
  // HTML content from accordion blocks rather than tabular rows.
  window.pdDetails = () => ({
    open: '',
    accordionData: {},

    html(bid) {
      return this.accordionData[bid] ?? null;
    },

    init() {
      this.open = this.$el.dataset.openFirst === 'true' ? 'b1' : '';

      const blob = document.getElementById('product-details-json');
      if (!blob) return;

      const data = JSON.parse(blob.textContent);
      const cur = data.variants[data.default];
      if (cur) this.accordionData = cur.accordion ?? {};

      document.addEventListener('grouped-product:changed', (e) => {
        const d = data.variants[e.detail.variantId];
        if (d) this.accordionData = d.accordion ?? {};
      });
    },
  });
}
